import React, { MutableRefObject, useCallback, useEffect, useState } from 'react'
import { Theme, useMediaQuery } from '@mui/material'
import { useFrame } from '@react-three/fiber'
import { Group, MathUtils } from 'three'

import MobileVerticalModel from 'public/assets/3dmodels/mobile/MobileVerticalModel'
import MobileHorizontalModel from 'public/assets/3dmodels/mobile/MobileHorizontalModel'
import TabletHorizontalModel from 'public/assets/3dmodels/tablet/TabletHorizontalModel'
import LaptopModel from 'public/assets/3dmodels/laptop/LaptopModel'

import { toScreenPosition, toWorldPosition } from 'utils/threejsUtils'

import { I3DModelProps, IScreenMedia } from 'public/assets/3dmodels/types'
import { IDevices, IUpdateDeviceParams } from './types'
import { deviceScaleAndRotation } from './constants'
import TabletVerticalModel from 'public/assets/3dmodels/tablet/TabletVerticalModel'

const DEVICE_INITIAL_POSITION = {
  x: 0.7,
  y: 0,
  z: 2.3,
}

const DESKTOP_MIN_X_MULTIPLIER = 0.65
const MOBILE_MIN_X_MULTIPLIER = 0.5
const MAX_X_MULTIPLIER = 5

const Devices = ({
  projects,
  portfolioContainerRef,
  projectDeviceRefs,
  setProjectDeviceRef,
  cameraRef,
}: IDevices) => {
  const upSm = useMediaQuery<Theme>((theme) => theme.breakpoints.up('sm'))
  const upMd = useMediaQuery<Theme>((theme) => theme.breakpoints.up('md'))
  const upLg = useMediaQuery<Theme>((theme) => theme.breakpoints.up('lg'))
  const upXl = useMediaQuery<Theme>((theme) => theme.breakpoints.up('xl'))

  const [videoRefs, setVideoRefs] = useState<MutableRefObject<HTMLVideoElement>[]>([])
  const [areRefsLoaded, setAreRefsLoaded] = useState(false)

  useEffect(() => {
    setVideoRefs(
      projects.map(() => {
        const video = document.createElement('video')
        video.crossOrigin = 'anonymous'
        video.loop = true
        video.muted = true
        video.controls = false
        video.autoplay = true
        video.playsInline = true

        return { current: video }
      }),
    )
    setAreRefsLoaded(true)
  }, [projects])

  const updateDevice = useCallback(
    ({
      device,
      index,
      delta,
      windowWidth,
      containerHeight,
      scrollTop,
      shouldHideLastDevice,
      minX,
      maxX,
    }: IUpdateDeviceParams) => {
      if (!device || !cameraRef.current) return

      const currentVideo = videoRefs[index]?.current

      let newPosition = DEVICE_INITIAL_POSITION
      let smoothness = 3 // how fast the device should move through the screen

      if (index === scrollTop / containerHeight && !shouldHideLastDevice) {
        const screenPosition = toScreenPosition(
          device,
          cameraRef.current,
          windowWidth,
          containerHeight,
        )

        const sectionTop = index * containerHeight
        const sectionScrollProgress = (scrollTop - sectionTop) / containerHeight

        screenPosition.x = sectionScrollProgress * (maxX - minX) + minX

        newPosition = toWorldPosition(
          screenPosition,
          cameraRef.current,
          windowWidth,
          containerHeight,
        )

        smoothness = 10

        if (currentVideo && currentVideo.paused && currentVideo.src) {
          currentVideo.play()
        }
      } else {
        // device is off screen

        if (currentVideo && !currentVideo.paused) {
          currentVideo.pause()
          currentVideo.currentTime = 0
        }
      }

      device.position.set(
        MathUtils.damp(device.position.x, newPosition.x, smoothness, delta),
        MathUtils.damp(device.position.y, newPosition.y, smoothness, delta),
        MathUtils.damp(device.position.z, newPosition.z, smoothness, delta),
      )
    },
    [cameraRef, videoRefs],
  )

  const updateDevicePosition = useCallback(
    (delta: number) => {
      const portfolioContainer = portfolioContainerRef.current
      const camera = cameraRef.current

      if (!portfolioContainer || !camera) return

      const windowWidth = window.innerWidth
      const containerHeight = portfolioContainer.clientHeight
      const scrollTop = portfolioContainer.scrollTop
      const documentScrollTop = document.documentElement.scrollTop

      // minX should be the center of the screen for mobile and 70% of the screen width for desktop devices
      // minX sets the device position when scrollProgress is at 0%, which is the top of the project section
      // maxX is a value that is high enough for always keeping the device offscreen
      const minX = windowWidth * (upLg ? DESKTOP_MIN_X_MULTIPLIER : MOBILE_MIN_X_MULTIPLIER)
      const maxX = windowWidth * MAX_X_MULTIPLIER

      portfolioContainer.style.overflowY = documentScrollTop > 0 ? 'hidden' : 'scroll'

      projectDeviceRefs.current.forEach((device, index, array) => {
        const shouldHideLastDevice = index + 1 === array.length && documentScrollTop > 170

        updateDevice({
          device,
          index,
          delta,
          windowWidth,
          containerHeight,
          scrollTop,
          shouldHideLastDevice,
          minX,
          maxX,
        })
      })
    },
    [portfolioContainerRef, projectDeviceRefs, cameraRef, updateDevice, upLg],
  )

  useFrame((state, delta) => {
    updateDevicePosition(delta)
  })

  const getDeviceScaleAndRotation = useCallback(
    (project: IProject) => {
      const currentDeviceProps = deviceScaleAndRotation[project.deviceType]

      if (upXl) return currentDeviceProps.xl
      if (upLg) return currentDeviceProps.lg
      if (upMd) return currentDeviceProps.md
      if (upSm) return currentDeviceProps.sm

      return currentDeviceProps.xs
    },
    [upSm, upMd, upLg, upXl],
  )

  const getDevice = useCallback(
    (index: number, device: string) => {
      const currentProject = projects[index]
      const currentVideoRef = videoRefs[index]
      const contentId = currentProject.id + (currentProject.showcaseVideo ? '-video' : '-image')

      const deviceScaleAndRotation = getDeviceScaleAndRotation(currentProject)

      const props = {
        ...deviceScaleAndRotation,
        position: [DEVICE_INITIAL_POSITION.x, DEVICE_INITIAL_POSITION.y, DEVICE_INITIAL_POSITION.z], // devices start off screen
        key: index,
        setRef: (ref: Group | null) => setProjectDeviceRef(index, ref),
        screenMedia: {
          type: currentProject.showcaseVideo ? 'video' : 'image',
          src: currentProject.showcaseVideo || currentProject?.showcaseImage?.fullSize,
          ref: currentVideoRef,
          contentId,
        } as IScreenMedia,
      } as I3DModelProps

      switch (device) {
        case 'mobile_portrait':
          return <MobileVerticalModel {...props} />
        case 'mobile_landscape':
          return <MobileHorizontalModel {...props} leftPos={0.17} />
        case 'tablet_landscape':
          return <TabletHorizontalModel {...props} />
        case 'tablet_portrait':
          return <TabletVerticalModel {...props} />
        case 'desktop':
          return <LaptopModel {...props} />
      }
    },
    [projects, setProjectDeviceRef, videoRefs, getDeviceScaleAndRotation],
  )

  if (!areRefsLoaded) return null

  return <group>{projects.map(({ deviceType }, index) => getDevice(index, deviceType))}</group>
}

export default Devices
